import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Truck, ArrowLeft, ShieldCheck, ShieldAlert, History } from 'lucide-react'
import DashboardLayout from '../../layouts/DashboardLayout'
import { cn } from '../../lib/utils'

// SUPPLIERS owner screen (ordernow1021a2, Paul 21 Oct 2026). Behind the owner PIN / admin role. Edit name, aliases,
// short code (used in PO numbers), WhatsApp phone, email, channel, excluded, delivery fee and free-delivery threshold
// (both ex VAT), and "contact verified". Every change is logged with who and when (shown under each supplier).
// Changing a phone or email clears "verified" unless the same save ticks it again. Nothing is sent from here.

const INPUT = 'w-full rounded-xl border border-brand-border bg-white px-3 py-2 text-base text-brand-ink focus:border-brand-primary focus:outline-none'

export default function Suppliers({ suppliers = [], channels = [], default_name = '' }) {
  const [name, setName] = useState(default_name || '')
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-3 py-4 sm:px-6">
        <Link href="/stock_takes" className="mb-3 inline-flex items-center gap-1 text-sm text-brand-muted"><ArrowLeft size={14} /> Stock takes</Link>
        <h1 className="flex items-center gap-2 text-lg font-semibold text-brand-ink"><Truck size={18} /> Suppliers</h1>
        <p className="mt-1 text-xs text-brand-muted">Owner only. Every change is logged with your name and the time.</p>
        <label className="mt-3 block text-xs font-medium text-brand-muted">Your name
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={cn(INPUT, 'mt-1 sm:max-w-xs')} />
        </label>
        <div className="mt-4 divide-y divide-brand-border overflow-hidden rounded-2xl border border-brand-border bg-white">
          {suppliers.length === 0 && <p className="px-4 py-3 text-sm text-brand-muted">No suppliers yet.</p>}
          {suppliers.map((s) => <SupplierRow key={s.id} s={s} channels={channels} name={name} />)}
        </div>
      </div>
    </DashboardLayout>
  )
}

function SupplierRow({ s, channels, name }) {
  const [open, setOpen] = useState(false)
  const [showLog, setShowLog] = useState(false)
  const [busy, setBusy] = useState(false)
  const [f, setF] = useState({
    supplier_name: s.name, aliases: (s.aliases || []).join(', '), short_code: s.short_code,
    whatsapp_phone: s.whatsapp_phone || '', email: s.email || '', channel: s.channel, excluded: !!s.excluded,
    delivery_fee: s.delivery_fee || '', free_delivery_threshold: s.free_delivery_threshold || '', contact_verified: false,
  })
  const set = (k) => (e) => setF((cur) => ({ ...cur, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))
  function save() {
    if (busy || !name.trim()) return
    setBusy(true)
    const body = { ...f, name: name.trim() }
    if (!f.contact_verified) delete body.contact_verified
    router.patch(`/suppliers/${s.id}`, body, { preserveScroll: true, onFinish: () => setBusy(false) })
  }
  return (
    <div className="px-4 py-3 text-sm">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-start justify-between gap-3 text-left">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">{s.name} <span className="text-xs font-normal text-brand-muted">{s.short_code} · {s.channel} · {s.item_count} items</span></p>
          <p className={cn('mt-0.5 flex items-center gap-1 text-xs', s.contact_verified ? 'text-emerald-800' : 'text-amber-800')}>
            {s.contact_verified ? <ShieldCheck size={13} /> : <ShieldAlert size={13} />}
            {s.contact_verified ? `contact verified by ${s.contact_verified_by}` : 'no verified contact'}
            {s.excluded ? ' · excluded: order outside, then log it' : ''}
          </p>
        </div>
        <span className="shrink-0 rounded-lg border border-brand-border px-2 py-1 text-xs font-semibold text-brand-ink">{open ? 'Close' : 'Edit'}</span>
      </button>
      {open && (
        <div className="mt-2 grid gap-2">
          <label className="text-xs text-brand-muted">Name<input type="text" value={f.supplier_name} onChange={set('supplier_name')} className={cn(INPUT, 'mt-1')} /></label>
          <label className="text-xs text-brand-muted">Other spellings (comma separated)<input type="text" value={f.aliases} onChange={set('aliases')} className={cn(INPUT, 'mt-1')} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-brand-muted">Short code (3 letters)<input type="text" maxLength={3} value={f.short_code} onChange={set('short_code')} className={cn(INPUT, 'mt-1 uppercase')} /></label>
            <label className="text-xs text-brand-muted">Channel
              <select value={f.channel} onChange={set('channel')} className={cn(INPUT, 'mt-1')}>
                {channels.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          </div>
          <label className="text-xs text-brand-muted">WhatsApp phone (rep)<input type="tel" value={f.whatsapp_phone} onChange={set('whatsapp_phone')} className={cn(INPUT, 'mt-1')} /></label>
          <label className="text-xs text-brand-muted">Email<input type="email" value={f.email} onChange={set('email')} className={cn(INPUT, 'mt-1')} /></label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-brand-muted">Delivery fee (R, ex VAT)<input inputMode="decimal" value={f.delivery_fee} onChange={set('delivery_fee')} className={cn(INPUT, 'mt-1')} /></label>
            <label className="text-xs text-brand-muted">Free delivery from (R, ex VAT)<input inputMode="decimal" value={f.free_delivery_threshold} onChange={set('free_delivery_threshold')} className={cn(INPUT, 'mt-1')} /></label>
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-ink"><input type="checkbox" checked={f.excluded} onChange={set('excluded')} /> Excluded (order outside, then log it)</label>
          <label className="flex items-center gap-2 text-sm text-brand-ink"><input type="checkbox" checked={f.contact_verified} onChange={set('contact_verified')} /> I checked this contact is the supplier's real rep / address</label>
          <button type="button" onClick={save} disabled={busy || !name.trim()}
            className="rounded-xl bg-brand-primary px-4 py-2.5 text-sm font-semibold text-white disabled:bg-slate-200 disabled:text-brand-muted">
            {busy ? 'Saving…' : 'Save'}
          </button>
          {!name.trim() && <p className="text-xs text-brand-muted">Type your name above first.</p>}
          <button type="button" onClick={() => setShowLog(!showLog)} className="inline-flex items-center gap-1 text-xs text-brand-muted"><History size={13} /> {showLog ? 'Hide' : 'Show'} change log ({(s.changes || []).length})</button>
          {showLog && (
            <ul className="space-y-1 text-xs text-brand-muted">
              {(s.changes || []).map((c, i) => (
                <li key={i}>{String(c.at).slice(0, 16).replace('T', ' ')} · {c.by} · {c.field}: {c.old_value || '-'} → {c.new_value || '-'}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
